
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "../../../components/AppToast";
import {
  getBrand,
  uploadBrandLogo,
  deleteBrandLogo,
} from "../../../services/brand/brand";
import PreferencesCard from "../../Settings/PreferencesCard";
import "./BrandManagement.css";

const getId = (value) => {
  if (!value) return "";
  if (typeof value === "object") return value._id || value.id || "";
  return String(value);
};

const BrandManagement = () => {
  const fileInputRef = useRef(null);
  const selectedFileRef = useRef(null);

  const { userDetail } = useSelector((state) => state.login);

  const userType = String(
    userDetail?.role || userDetail?.userType || ""
  ).toUpperCase();

  const isTenantAdmin = userType === "TENANT_ADMIN";
  const isPartner = userType === "PARTNER";

  const scopeType = isTenantAdmin
    ? "TENANT"
    : isPartner
      ? "PARTNER"
      : "";

  const scopeId = isTenantAdmin
    ? getId(userDetail?.tenantId)
    : isPartner
      ? getId(userDetail?.partnerId || userDetail?.partner)
      : "";

  const scopeLabel = isTenantAdmin ? "Tenant" : "Partner";
  const logoLabel = isTenantAdmin ? "Tenant logo" : "Partner logo";

  const [activeSection, setActiveSection] = useState("brand");
  const [brand, setBrand] = useState(null);
  const [brandImage, setBrandImage] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [loading, setLoading] = useState(true);
  const [savingLogo, setSavingLogo] = useState(false);
  const [removingLogo, setRemovingLogo] = useState(false);

  const loadBrand = useCallback(async () => {
    setLoading(true);

    try {
      const data = await getBrand();

      setBrand(data || null);
      setBrandImage(data?.brandImage || "");

      selectedFileRef.current = null;
      setSelectedFile(null);

      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    } catch (error) {
      console.error("Failed to fetch branding:", error);
      toast.error(error.message || "Failed to load branding.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!scopeType || !scopeId) {
      setLoading(false);
      return;
    }

    loadBrand();
  }, [scopeType, scopeId, loadBrand]);

  useEffect(() => {
    if (!selectedFile) {
      setPreviewUrl(brandImage || "");
      return;
    }

    const objectUrl = URL.createObjectURL(selectedFile);
    setPreviewUrl(objectUrl);

    return () => URL.revokeObjectURL(objectUrl);
  }, [selectedFile, brandImage]);

  const handleImageChange = (event) => {
    const file = event.target.files?.[0];

    if (!file) return;

    const allowedTypes = ["image/png", "image/jpeg", "image/webp"];

    if (!allowedTypes.includes(file.type)) {
      toast.error("Only PNG, JPG, and WEBP images are allowed.");
      event.target.value = "";
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image size must not exceed 5 MB.");
      event.target.value = "";
      return;
    }

    // Store the actual file in both state and ref.
    selectedFileRef.current = file;
    setSelectedFile(file);
  };

  const handleRemoveImage = async () => {
    // Remove a newly selected image from the preview without calling the API.
    const currentFile = selectedFileRef.current || selectedFile;

    if (currentFile) {
      selectedFileRef.current = null;
      setSelectedFile(null);

      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }

      return;
    }

    // No persisted logo to delete.
    if (!brand?.brandObjectName) {
      setBrandImage("");
      setPreviewUrl("");

      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }

      return;
    }

    try {
      setRemovingLogo(true);

      await deleteBrandLogo();

      setBrand((previous) =>
        previous
          ? {
              ...previous,
              brandImage: null,
              brandObjectName: null,
              originalFileName: null,
              mimeType: null,
              fileSize: null,
            }
          : null
      );

      selectedFileRef.current = null;
      setBrandImage("");
      setSelectedFile(null);
      setPreviewUrl("");

      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }

      toast.success("Brand logo removed successfully.");
    } catch (error) {
      console.error("Failed to remove logo:", error);
      toast.error(error.message || "Failed to remove logo.");
    } finally {
      setRemovingLogo(false);
    }
  };

  const handleSaveLogo = async () => {
    if (!isTenantAdmin && !isPartner) {
      toast.error(
        "Only tenant administrators and partners can manage branding."
      );
      return;
    }

    if (!scopeId) {
      toast.error(`Unable to identify the ${scopeLabel.toLowerCase()}.`);
      return;
    }

    // Read the latest selected file.
    const file = selectedFileRef.current || selectedFile;

    if (!(file instanceof File)) {
      console.error("Selected file state:", selectedFile);
      console.error("Selected file ref:", selectedFileRef.current);

      toast.error("Please select a logo image before saving.");
      return;
    }

    try {
      setSavingLogo(true);

   

      const response = await uploadBrandLogo(file);

      // The service may return the full API response or its data object.
      const savedBrand =
        response?.data?.data || response?.data || response;

      setBrand((previous) => ({
        ...(previous || {}),
        ...(savedBrand || {}),
      }));

      setBrandImage(savedBrand?.brandImage || "");

      selectedFileRef.current = null;
      setSelectedFile(null);

      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }

      toast.success(
        response?.message || `${scopeLabel} logo saved successfully.`
      );
    } catch (error) {
      console.error("Failed to save logo:", error);
      toast.error(
        error.message ||
          `Failed to save ${scopeLabel.toLowerCase()} logo.`
      );
    } finally {
      setSavingLogo(false);
    }
  };

  if (!isTenantAdmin && !isPartner) {
    return (
      <div className="user-settings admin-settings brand-management">
        <div className="settings-panel">
          <div className="settings-panel__body">
            <h2>Brand Management</h2>
            <p>
              Branding and workspace preferences are available to tenant
              administrators and partners only.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="user-settings admin-settings brand-management">
        <div className="settings-panel">
          <div className="settings-panel__body">
            <p>Loading branding and preferences...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="user-settings admin-settings brand-management">
      <aside className="settings-rail">
        <div className="settings-identity">
          <div className="settings-avatar">
            <i className="bi bi-palette2" />
          </div>

          <div className="settings-identity__copy">
            <h1>{scopeLabel} Management</h1>
            <p>{userType.replaceAll("_", " ")}</p>
            <span className="settings-org">
              Manage your {scopeLabel.toLowerCase()} logo and preferences
            </span>
          </div>
        </div>

        <nav className="settings-nav" aria-label={`${scopeLabel} settings`}>
          <button
            type="button"
            className={`settings-nav__item ${
              activeSection === "brand" ? "is-active" : ""
            }`}
            onClick={() => setActiveSection("brand")}
            aria-current={activeSection === "brand" ? "page" : undefined}
          >
            <span className="settings-nav__icon">
              <i className="bi bi-image" />
            </span>
            <span>
              <strong>{logoLabel}</strong>
              <small>Logo &amp; branding</small>
            </span>
          </button>

          <button
            type="button"
            className={`settings-nav__item ${
              activeSection === "preferences" ? "is-active" : ""
            }`}
            onClick={() => setActiveSection("preferences")}
            aria-current={
              activeSection === "preferences" ? "page" : undefined
            }
          >
            <span className="settings-nav__icon">
              <i className="bi bi-sliders" />
            </span>
            <span>
              <strong>Preferences</strong>
              <small>Workspace settings</small>
            </span>
          </button>
        </nav>
      </aside>

      <section className="settings-panel" aria-live="polite">
        {activeSection === "brand" ? (
          <>
            <header className="settings-panel__head">
              <p className="settings-kicker">Branding</p>
              <h2>{logoLabel}</h2>
              <p className="settings-lead">
                Upload and preview your {scopeLabel.toLowerCase()} logo
                before saving.
              </p>
            </header>

            <div className="settings-panel__body">
              <div className="brand-form-card">
                <div className="brand-form-heading">
                  <div>
                    <h3>{logoLabel}</h3>
                    <p>
                      Upload the logo displayed for your{" "}
                      {scopeLabel.toLowerCase()}.
                    </p>
                  </div>

                  <span className="brand-status">
                    <i className="bi bi-pencil-square" />
                    Editable
                  </span>
                </div>

                <div className="brand-upload-section">
                  <label className="brand-field-label">
                    {logoLabel}
                  </label>

                  <div className="brand-upload-layout">
                    <div className="brand-preview">
                      {previewUrl ? (
                        <img src={previewUrl} alt={logoLabel} />
                      ) : (
                        <div className="brand-empty-preview">
                          <i className="bi bi-image" />
                          <span>No logo uploaded</span>
                        </div>
                      )}
                    </div>

                    <div className="brand-upload-content">
                      <h4>Upload {scopeLabel.toLowerCase()} logo</h4>
                      <p>
                        Choose an image from your device. The preview
                        updates immediately before saving.
                      </p>

                      <p className="brand-file-hint">
                        PNG, JPG or WEBP · Maximum 5 MB
                      </p>

                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/png,image/jpeg,image/webp"
                        hidden
                        onChange={handleImageChange}
                      />

                      <div className="brand-upload-actions">
                        <button
                          type="button"
                          className="brand-primary-btn"
                          disabled={savingLogo || removingLogo}
                          onClick={() => fileInputRef.current?.click()}
                        >
                          <i className="bi bi-cloud-arrow-up" />
                          {previewUrl ? "Replace logo" : "Upload logo"}
                        </button>

                        {previewUrl && (
                          <button
                            type="button"
                            className="brand-remove-btn"
                            disabled={savingLogo || removingLogo}
                            onClick={handleRemoveImage}
                          >
                            <i className="bi bi-trash3" />
                            {removingLogo ? "Removing..." : "Remove"}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="brand-preview-section">
                  <div className="brand-preview-heading">
                    <h3>Live preview</h3>
                    <span>Preview</span>
                  </div>

                  <div className="brand-preview-banner">
                    <div className="brand-preview-logo">
                      {previewUrl ? (
                        <img src={previewUrl} alt="Logo preview" />
                      ) : (
                        <i className="bi bi-image" />
                      )}
                    </div>

                    <div className="brand-preview-copy">
                      <strong>{logoLabel}</strong>
                      <span>{scopeLabel} branding preview</span>
                    </div>
                  </div>
                </div>

                <div className="brand-form-footer">
                  <button
                    type="button"
                    className="brand-primary-btn brand-save-btn"
                    disabled={
                      savingLogo ||
                      removingLogo ||
                      !(selectedFileRef.current || selectedFile)
                    }
                    onClick={handleSaveLogo}
                  >
                    <i className="bi bi-check2-circle" />
                    {savingLogo ? "Saving..." : "Save logo"}
                  </button>
                </div>
              </div>
            </div>
          </>
        ) : (
          <>
            <header className="settings-panel__head">
              <p className="settings-kicker">Workspace</p>
              <h2>{scopeLabel} Preferences</h2>
              <p className="settings-lead">
                Manage language, notifications, and appearance preferences
                for this {scopeLabel.toLowerCase()}.
              </p>
            </header>

            <div className="settings-panel__body">
              <PreferencesCard
                key={`${scopeType}_${scopeId}`}
                scopeType={scopeType}
                scopeId={scopeId}
                initialPreferences={brand}
                onSaved={(updatedPreferences) => {
                  setBrand((previous) => ({
                    ...(previous || {}),
                    ...(updatedPreferences || {}),
                  }));

                  toast.success("Preferences saved successfully.");
                }}
              />
            </div>
          </>
        )}
      </section>
    </div>
  );
};

export default BrandManagement;
